import { useCallback, useEffect, useRef, useState } from 'react';
import type { Ride } from '../domain/ride';
import { draftStorageKey, loadStudioDraft, saveStudioDraft } from './draft-storage';
import type { StudioDraft } from './studio-draft';
import { RECOVERY_MESSAGE, draftValidationIssue, type DraftValidationIssue } from './draft-recovery';
import { createStudioProjectSync, studioProjectSession } from '../services/studio-project';

/** Persists accepted edits immediately; playback writes only on pause or navigation. */
export function useStudioDraft(ride: Ride, configuration?: StudioDraft, initialSource?: 'file') {
  const [initial] = useState(() => loadStudioDraft(ride, location.search, configuration, initialSource === 'file'));
  const [draft, setDraft] = useState(initial.draft);
  const [message, setMessage] = useState(initial.message);
  const [needsReview, setNeedsReview] = useState(initial.needsReview);
  const [issues, setIssues] = useState(initial.issues);
  const [reviewOpen, setReviewOpen] = useState(initial.needsReview);
  const [reviewError, setReviewError] = useState('');
  const [validationIssue, setValidationIssue] = useState<DraftValidationIssue | null>(null);
  const [conflict, setConflict] = useState(false);
  const [projectSync] = useState(() => {
    const project = studioProjectSession(ride);
    return project ? createStudioProjectSync(ride, project, setMessage, () => setConflict(true)) : null;
  });
  const current = useRef(draft);
  const saved = useRef(initial.saved);
  const dirty = useRef(false);
  const review = useRef(initial.needsReview);
  const persist = useCallback((): boolean => {
    try {
      saved.current = saveStudioDraft(ride, current.current, saved.current);
      projectSync?.save(current.current);
      dirty.current = false; setMessage(''); setConflict(false); return true;
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'Browser storage is unavailable. Settings remain in this session.');
      try { setConflict(localStorage.getItem(draftStorageKey(ride)) !== saved.current); } catch { /* Storage unavailable. */ }
      return false;
    }
  }, [ride.sourceFingerprint, ride.id, projectSync]);
  const flush = useCallback((): boolean => {
    if (!dirty.current || review.current) return !review.current;
    return persist();
  }, [persist]);
  const update = useCallback((value: StudioDraft | ((value: StudioDraft) => StudioDraft), persist = true) => {
    current.current = typeof value === 'function' ? value(current.current) : value;
    dirty.current = true; setDraft(current.current);
    return persist ? flush() : true;
  }, [flush]);
  useEffect(() => {
    const onHidden = () => { if (document.visibilityState === 'hidden') flush(); };
    const onStorage = (event: StorageEvent) => {
      if (event.key !== draftStorageKey(ride) || event.newValue === saved.current) return;
      setConflict(true); setMessage('Studio settings changed in another tab. Load those settings or keep your current edits.');
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('pagehide', flush); document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('storage', onStorage); flush(); };
  }, [flush, ride.sourceFingerprint, ride.id]);
  return { draft, update, flush, message, needsReview, conflict, issues, reviewOpen, reviewError, validationIssue,
    renderProject: projectSync ? () => projectSync.render() : undefined,
    openReview: () => { setReviewError(''); setValidationIssue(null); setReviewOpen(true); },
    closeReview: () => setReviewOpen(false),
    acceptRecovered: () => {
      const invalid = draftValidationIssue(ride, current.current);
      setValidationIssue(invalid); setReviewError('');
      if (invalid) return false;
      dirty.current = true;
      if (!persist()) {
        setReviewError('The composition could not be saved. Resolve the storage error or saved-settings conflict below, then try again.');
        return false;
      }
      review.current = false; setNeedsReview(false); setIssues([]); setReviewOpen(false);
      return true;
    },
    loadLatest: () => {
      if (projectSync) {
        void projectSync.refresh().then(loaded => {
          saved.current = localStorage.getItem(draftStorageKey(ride)); current.current = loaded.draft;
          saved.current = saveStudioDraft(ride, loaded.draft, saved.current); dirty.current = false;
          setDraft(loaded.draft); setConflict(false); setMessage('');
        }).catch(cause => setMessage(cause instanceof Error ? cause.message : 'The saved project could not be loaded.'));
        return;
      }
      const loaded = loadStudioDraft(ride, location.search, configuration);
      current.current = loaded.draft; saved.current = loaded.saved; dirty.current = false;
      review.current = loaded.needsReview; setDraft(loaded.draft); setMessage(loaded.message);
      setNeedsReview(loaded.needsReview); setIssues(loaded.issues); setReviewOpen(loaded.needsReview);
      setReviewError(''); setValidationIssue(null); setConflict(false);
    },
    keepCurrent: () => { if (projectSync) {
      void projectSync.refresh().then(() => { saved.current = localStorage.getItem(draftStorageKey(ride)); dirty.current = true; setConflict(false); flush(); })
        .catch(cause => setMessage(cause instanceof Error ? cause.message : 'The latest project revision could not be read.'));
      return;
    } try { saved.current = localStorage.getItem(draftStorageKey(ride)); dirty.current = true;
      if (review.current) { setConflict(false); setMessage(RECOVERY_MESSAGE); setReviewError(''); } else flush(); }
      catch { setMessage('Browser storage is unavailable. Settings remain in this session.'); } },
  };
}
